import React, { useEffect, useState } from 'react';
import { DashboardCard } from '../components/DashboardCard';
import { Smartphone, Database, Loader2 } from 'lucide-react';
import { getApiUrl } from '../utils/api';

interface DatabaseInfo {
  name: string;
  hasOrdersCampaigns: boolean;
}

// Исключаемые базы данных
const EXCLUDED_DATABASES = [
  'analytics_db',
  'benchmark',
  'operative',
  'versioned',
  'INFORMATION_SCHEMA',
  'information_schema',
  'system',
  'default'
];

const HomePage: React.FC = () => {
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Скрываем Old Tables по умолчанию
  const [showOldTables, setShowOldTables] = useState(false);

  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        // Получаем список баз данных через backend API
        const response = await fetch(getApiUrl('/api/databases'));

        if (!response.ok) {
          throw new Error('Failed to fetch databases');
        }

        const result = await response.json();

        if (result.success && result.data) {
          const validDatabases: DatabaseInfo[] = result.data.map((name: string) => ({
            name,
            hasOrdersCampaigns: true
          }));
          setDatabases(validDatabases);
        } else {
          setDatabases([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchDatabases();
  }, []);

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: '22px',
    fontWeight: 600,
    marginBottom: '20px',
    marginTop: '40px',
    color: 'var(--text-primary)',
    borderBottom: '2px solid var(--border-color, #e0e0e0)',
    paddingBottom: '10px',
  };

  const toggleButtonStyle: React.CSSProperties = {
    background: 'none',
    border: '1px solid var(--border-color, #e0e0e0)',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    marginTop: '40px',
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      padding: '0 15px'
    }}>
      <div style={{
        width: '100%',
        margin: '0 auto',
        padding: '40px 0',
      }}>
        <header style={{
          textAlign: 'left',
          marginBottom: '40px'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            marginBottom: '10px',
            color: 'var(--text-primary)'
          }}>
            Analytics
          </h1>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '16px'
          }}>
            Databases with orders_campaigns table
          </p>
        </header>

        {/* New Tables Section */}
        <section>
          <h2 style={sectionHeaderStyle}>Databases</h2>

          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'var(--text-secondary)',
              padding: '20px 0'
            }}>
              <Loader2 style={{ animation: 'spin 1s linear infinite' }} size={20} />
              <span>Loading databases...</span>
            </div>
          )}

          {error && (
            <div style={{
              color: '#ef4444',
              padding: '15px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              Error: {error}
            </div>
          )}

          {!loading && !error && databases.length === 0 && (
            <div style={{
              color: 'var(--text-secondary)',
              padding: '20px 0'
            }}>
              No databases with orders_campaigns table found
            </div>
          )}

          <div>
            {databases.map((db) => (
              <div key={db.name} style={{ marginBottom: '30px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '15px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Database size={20} />
                  {db.name}
                </h3>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <DashboardCard
                    title="Campaigns"
                    description="orders_campaigns"
                    route={`/db/${db.name}`}
                    icon={<Database size={18} />}
                  />
                  <DashboardCard
                    title="Adsets"
                    description="orders_adsets"
                    route={`/db/${db.name}/adsets`}
                    icon={<Database size={18} />}
                  />
                  <DashboardCard
                    title="Ads"
                    description="orders_ads"
                    route={`/db/${db.name}/ads`}
                    icon={<Database size={18} />}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Legacy Toggle Button */}
        <button
          style={toggleButtonStyle}
          onClick={() => setShowOldTables(!showOldTables)}
        >
          {showOldTables ? 'Hide Legacy Tables' : 'Show Legacy Tables'}
        </button>

        {/* Old Tables Section - Hidden by default */}
        {showOldTables && (
          <section>
            <h2 style={sectionHeaderStyle}>Legacy Tables</h2>

            <div>
              <DashboardCard
                title="GoLive Dashboard Android"
                description=""
                route="/android-dashboard"
                icon={<Smartphone />}
              />
              <br />
              <DashboardCard
                title="Omego Dashboard Android"
                description=""
                route="/android-omego-dashboard"
                icon={<Smartphone />}
              />
              <br />
              <DashboardCard
                title="GoLive V2 Dashboard Android"
                description=""
                route="/android-dashboard-v2"
                icon={<Smartphone />}
              />
              <br />
              <DashboardCard
                title="GoLive Web"
                description=""
                route="/web"
                icon={<Smartphone />}
              />
              <br />
              <DashboardCard
                title="Yess"
                description=""
                route="/yess"
                icon={<Smartphone />}
              />
              <DashboardCard
                title="WowClip"
                description=""
                route="/wow"
                icon={<Smartphone />}
              />
              <br />
              <DashboardCard
                title="Cleep AI LP2"
                description="ClickHouse Analytics (localhost:3028)"
                route="/cleep-ai-lp2"
                icon={<Database />}
              />
            </div>
          </section>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
